import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const STEPS = ['Welcome', 'Birthday', 'Username', 'About You'];

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useContext(AuthContext);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: user?.name || '',
    birthday: '',
    username: '',
    bio: '',
    interests: [],
    hobbies: [],
  });
  const [saving, setSaving] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  const INTEREST_OPTIONS = ['Coffee', 'Wine', 'Hiking', 'Beach', 'Concerts', 'Museums', 'Foodie', 'Fitness', 'Books', 'Nature', 'Travel', 'Art'];
  const HOBBY_OPTIONS = ['Reading', 'Cooking', 'Gaming', 'Music', 'Sports', 'Dancing', 'Photography', 'Movies', 'Yoga', 'Gardening'];

  const toggleItem = (arr, item) => arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];

  const checkUsername = async (username) => {
    if (!username || username.length < 3 || !supabase) { setUsernameAvailable(null); return; }
    setCheckingUsername(true);
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('username', username.toLowerCase())
        .neq('id', user?.id)
        .maybeSingle();
      setUsernameAvailable(!data);
    } catch (e) { setUsernameAvailable(null); }
    setCheckingUsername(false);
  };

  const handleUsernameChange = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9_.]/g, '');
    setForm({ ...form, username: clean });
    if (clean.length >= 3) {
      setTimeout(() => checkUsername(clean), 500);
    } else {
      setUsernameAvailable(null);
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleFinish();
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    await updateProfile({
      name: form.name,
      birthday: form.birthday || null,
      username: form.username || null,
      bio: form.bio,
      interests: form.interests,
      hobbies: form.hobbies,
      onboarding_complete: true,
    });
    setSaving(false);
    navigate('/home', { replace: true });
  };

  const canProceed = () => {
    switch (step) {
      case 0: return form.name.trim().length > 0;
      case 1: return !!form.birthday;
      case 2: return !form.username || (form.username.length >= 3 && usernameAvailable !== false);
      case 3: return true;
      default: return true;
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Progress */}
      <div className="w-full bg-gray-100 h-1.5">
        <div
          className="bg-gradient-to-r from-pink-500 to-rose-500 h-1.5 transition-all duration-500"
          style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      <div className="flex-1 p-6 flex flex-col">
        {/* Skip button */}
        <div className="flex justify-end mb-4">
          <button onClick={() => navigate('/home')} className="text-sm text-gray-400 hover:text-gray-600">
            Skip for now
          </button>
        </div>

        {/* Step 0: Welcome */}
        {step === 0 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex-1 flex flex-col">
            <div className="text-5xl mb-6">👋</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome to DayClap!</h2>
            <p className="text-gray-500 mb-8 leading-relaxed">
              Let's set up your profile so you can create beautiful invitations. This only takes a minute.
            </p>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">What should we call you?</label>
              <div className="relative">
                <SafeIcon name="User" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none text-lg"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 1: Birthday */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex-1 flex flex-col">
            <div className="text-5xl mb-6">🎂</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">When's your birthday?</h2>
            <p className="text-gray-500 mb-8 leading-relaxed">
              We'll use this to surprise you on your special day!
            </p>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Birthday</label>
              <input
                type="date" value={form.birthday}
                onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none text-lg"
              />
            </div>
          </motion.div>
        )}

        {/* Step 2: Username */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex-1 flex flex-col">
            <div className="text-5xl mb-6">🔍</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Create your username</h2>
            <p className="text-gray-500 mb-8 leading-relaxed">
              Your partner can search for you using this username to connect your profiles.
            </p>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">@</span>
                <input
                  type="text" value={form.username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="yourname"
                  className="w-full pl-10 pr-12 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none text-lg"
                />
                {checkingUsername && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-gray-200 border-t-pink-500 rounded-full animate-spin" />
                )}
                {!checkingUsername && usernameAvailable === true && (
                  <SafeIcon name="CheckCircle" className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500 text-xl" />
                )}
                {!checkingUsername && usernameAvailable === false && (
                  <SafeIcon name="XCircle" className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500 text-xl" />
                )}
              </div>
              {usernameAvailable === true && (
                <p className="text-green-600 text-sm mt-2 flex items-center gap-1">
                  <SafeIcon name="Check" className="text-xs" /> Username is available!
                </p>
              )}
              {usernameAvailable === false && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                  <SafeIcon name="X" className="text-xs" /> Username is taken. Try another!
                </p>
              )}
              <p className="text-gray-400 text-xs mt-2">Lowercase letters, numbers, dots, and underscores only</p>
            </div>
            <div className="bg-pink-50 border border-pink-100 rounded-xl p-4 mt-6">
              <div className="flex items-start gap-3">
                <SafeIcon name="Info" className="text-pink-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-pink-800 mb-1">Why a username?</p>
                  <p className="text-xs text-pink-600 leading-relaxed">
                    Your significant other can search for your username to find and connect with you. Once matched, you'll appear as a couple on each other's profiles!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: About You */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex-1 flex flex-col overflow-y-auto">
            <div className="text-5xl mb-6">✨</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Tell us about you</h2>
            <p className="text-gray-500 mb-6 leading-relaxed">
              This helps personalize your invitations. All optional!
            </p>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Short bio</label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="e.g. Coffee lover & sunset chaser"
                rows={2}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none resize-none"
              />
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Interests</label>
              <div className="flex flex-wrap gap-2">
                {INTEREST_OPTIONS.map((i) => (
                  <button
                    key={i} type="button"
                    onClick={() => setForm({ ...form, interests: toggleItem(form.interests, i) })}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      form.interests.includes(i)
                        ? 'bg-pink-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hobbies</label>
              <div className="flex flex-wrap gap-2">
                {HOBBY_OPTIONS.map((h) => (
                  <button
                    key={h} type="button"
                    onClick={() => setForm({ ...form, hobbies: toggleItem(form.hobbies, h) })}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      form.hobbies.includes(h)
                        ? 'bg-purple-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Navigation */}
        <div className="mt-auto pt-6 flex gap-3">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} className="flex-1 bg-gray-100 text-gray-700 font-bold py-4 rounded-xl">
              Back
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!canProceed() || saving}
            className={`${step === 0 ? 'w-full' : 'flex-[2]'} bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200 disabled:opacity-50 flex items-center justify-center gap-2`}
          >
            {saving ? (
              <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
            ) : step === STEPS.length - 1 ? (
              'Finish ✨'
            ) : (
              'Continue →'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;